export function objectIdNumberToString<T extends { id: number }>(object: T): T {
  return {
    ...object,
    id: String(object.id),
  };
}

export function objectIdStringToNumber<T extends { id: string }>(object: T): T {
  return {
    ...object,
    id: Number(object.id),
  };
}

export type Diff<T> = Partial<{
  [K in keyof T]: T[K] extends object ? (T[K] extends any[] ? never : Diff<T[K]>) : T[K];
}>;

function isPlainObject(val: unknown): val is Record<string, unknown> {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
}

export function objectDifference<T extends object>(base: T, updated: T): Diff<T> {
  const result: Partial<Record<keyof T, unknown>> = {};

  for (const key in base) {
    if (!(key in updated)) continue;

    const val1 = base[key];
    const val2 = updated[key];

    if (isPlainObject(val1) && isPlainObject(val2)) {
      const nestedDiff = objectDifference(val1, val2);
      if (Object.keys(nestedDiff).length > 0) {
        result[key] = nestedDiff;
      }
    } else if (val1 !== val2) {
      result[key] = val2;
    }
  }

  return result as Diff<T>;
}

export function applyDiff<T extends object>(base: T, diff: Diff<T>): T {
  const result: any = { ...base };

  for (const key in diff) {
    if (!diff.hasOwnProperty(key)) continue;

    const baseVal = base[key];
    const diffVal = diff[key];

    if (isPlainObject(baseVal) && isPlainObject(diffVal)) {
      result[key] = applyDiff(baseVal, diffVal as any);
    } else {
      result[key] = diffVal;
    }
  }

  return result;
}

export function isEmptyObject(o) {
  return o && Object.keys(o).length === 0 && o.constructor === Object;
}
